import React from 'react';
import './charts.css';

// Bloomberg-style live ticker bar. A horizontal row of metric cells that
// pulses (subtle border glow) for ~1s whenever its value changes — this is
// where the "live" feel comes from. Static layout (no scrolling animation)
// so the data is always readable.
//
// Props:
//   items: { key, label, value, suffix?, accent? }[]
//          key is used to detect value changes for the pulse
//          accent: 'gold' | 'green' | 'red' | 'amber' (defaults gold)
//   updatedAt: ISO timestamp shown on the right edge

const ACCENT_TONES = {
  gold:  '#00e0ff',
  green: '#34d399',
  red:   '#f87171',
  amber: '#fbbf24',
};

class LiveTickerCell extends React.Component {
  state = { pulse: false };
  componentDidUpdate(prev) {
    if (prev.item.value !== this.props.item.value) {
      this.setState({ pulse: true });
      clearTimeout(this._t);
      this._t = setTimeout(() => this.setState({ pulse: false }), 1000);
    }
  }
  componentWillUnmount() { clearTimeout(this._t); }
  render() {
    const { item } = this.props;
    const tone = ACCENT_TONES[item.accent] || ACCENT_TONES.gold;
    const clickable = typeof item.onClick === 'function';
    return (
      <div
        className={`live-ticker-cell ${this.state.pulse ? 'live-ticker-cell--pulse' : ''} ${clickable ? 'live-ticker-cell--clickable' : ''}`}
        style={{ '--tone': tone }}
        onClick={clickable ? item.onClick : undefined}
        role={clickable ? 'button' : undefined}
        tabIndex={clickable ? 0 : undefined}
        onKeyDown={clickable ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); item.onClick(); } } : undefined}
        title={clickable ? (item.hint || `View ${item.label}`) : undefined}
      >
        <span className="live-ticker-cell-label">{item.label}</span>
        <span className="live-ticker-cell-value" style={{ color: tone }}>
          {item.value}
          {item.suffix && <span className="live-ticker-cell-suffix">{item.suffix}</span>}
        </span>
      </div>
    );
  }
}

const LiveTicker = ({ items = [], updatedAt }) => {
  const stamp = updatedAt
    ? new Date(updatedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })
    : '';
  return (
    <div className="live-ticker">
      <div className="live-ticker-pulse" aria-hidden="true">
        <span className="live-ticker-pulse-dot" />
        <span className="live-ticker-pulse-label">LIVE</span>
      </div>
      <div className="live-ticker-cells">
        {items.map((it) => (
          <LiveTickerCell key={it.key} item={it} />
        ))}
      </div>
      {stamp && <div className="live-ticker-stamp">Updated {stamp}</div>}
    </div>
  );
};

export default LiveTicker;
